import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestoreIcon from "@mui/icons-material/Restore";
import api from "../../api/api";
import RentalTerms from '../../shared/RentalTerms';
import rentalTerms from '../../../legal/rentalTerms.json';

// Доступные плейсхолдеры — синхронизировано с build_offer_context() на бэке
const PLACEHOLDERS = [
  { key: "deal_number", desc: "Номер сделки" },
  { key: "contract_date", desc: "Дата договора (ДД.ММ.ГГГГ)" },
  { key: "start_date", desc: "Дата начала аренды" },
  { key: "end_date", desc: "Дата окончания аренды" },
  { key: "amount", desc: "Сумма аренды (₽)" },
  { key: "asset_name", desc: "Название товара/актива" },
  { key: "items_table", desc: "Таблица: товар, срок и стоимость по позициям" },
  { key: "specification_block", desc: "Спецификация товаров с общим итогом" },
  { key: "pickup_address", desc: "Адрес выдачи" },
  { key: "pickup_point_name", desc: "Название пункта выдачи" },
  { key: "pickup_block", desc: "Готовая строка «Место выдачи: ...»" },
  { key: "client_name", desc: "Имя арендатора" },
  { key: "client_phone", desc: "Телефон арендатора" },
  { key: "landlord_name", desc: "Наименование арендодателя (ИП)" },
  { key: "landlord_inn", desc: "ИНН арендодателя" },
  { key: "landlord_ogrnip", desc: "ОГРНИП арендодателя" },
  { key: "landlord_legal_address", desc: "Юр. адрес арендодателя" },
  { key: "landlord_phone", desc: "Телефон арендодателя" },
  { key: "landlord_working_hours", desc: "Режим работы" },
  { key: "landlord_city", desc: "Город арендодателя" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ru-RU");
  } catch {
    return iso;
  }
};

const shortHash = (h) => (h ? `${h.slice(0, 8)}…${h.slice(-4)}` : "—");

export default function OfferVersionsEditor({ expanded, onChange }) {
  const [versions, setVersions] = useState([]);
  const [loadError, setLoadError] = useState("");
  const selectionVersion = useRef(0);
  const listVersion = useRef(0);
  useEffect(() => () => { selectionVersion.current++; listVersion.current++; }, []);
  const [loading, setLoading] = useState(false);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorText, setEditorText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);

  const fetchVersions = useCallback(async () => {
    const version = ++listVersion.current;
    setLoading(true);
    setLoadError("");
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const res = await api.get("/offer-versions/", auth);
      if (version === listVersion.current) setVersions(res.data || []);
    } catch (e) {
      console.error("Ошибка загрузки версий оферты:", e);
      if (version === listVersion.current) setLoadError("Не удалось загрузить версии оферты");
    } finally {
      if (version === listVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) fetchVersions();
  }, [expanded, fetchVersions]);

  const openEditorWithActive = async () => {
    const version = ++selectionVersion.current;
    setLoadError("");
    setSaveError("");
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const res = await api.get("/offer-versions/active", auth);
      if (version !== selectionVersion.current) return;
      const text = res.data?.text || "";
      setEditorText(text);
      setEditorOpen(true);
    } catch (e) {
      console.error(e);
      if (version === selectionVersion.current) setLoadError("Не удалось загрузить текст оферты");
    }
  };

  const openEditorFromVersion = async (versionId) => {
    const version = ++selectionVersion.current;
    setLoadError("");
    setSaveError("");
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const res = await api.get(`/offer-versions/${versionId}`, auth);
      if (version !== selectionVersion.current) return;
      setEditorText(res.data?.text || "");
      setEditorOpen(true);
    } catch (e) {
      console.error(e);
      if (version === selectionVersion.current) setLoadError("Не удалось загрузить текст оферты");
    }
  };

  const handleSave = async () => {
    if (!editorText.trim()) {
      setSaveError("Текст оферты не может быть пустым");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      await api.post("/offer-versions/", { text: editorText }, auth);
      setEditorOpen(false);
      fetchVersions();
    } catch (e) {
      console.error("Ошибка сохранения оферты:", e);
      const detail = e?.response?.data?.detail;
      setSaveError(typeof detail === "string" ? detail : "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (versionId) => {
    if (!window.confirm("Сделать эту версию активной? Текущая активная будет деактивирована.")) return;
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      await api.post(`/offer-versions/${versionId}/activate`, {}, auth);
      fetchVersions();
    } catch (e) {
      console.error(e);
      alert("Ошибка активации версии");
    }
  };

  const openPreview = async (versionId) => {
    const version = ++selectionVersion.current;
    setLoadError("");
    try {
      const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const res = await api.get(`/offer-versions/${versionId}`, auth);
      if (version !== selectionVersion.current) return;
      setPreviewVersion(res.data);
      setPreviewOpen(true);
    } catch (e) {
      console.error(e);
      if (version === selectionVersion.current) setLoadError("Не удалось загрузить текст оферты");
    }
  };

  const insertPlaceholder = (key) => {
    setEditorText((prev) => `${prev}{${key}}`);
  };

  return (
    <Accordion expanded={expanded} onChange={onChange} sx={{ mt: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <Box>
            <Typography variant="h6">Оферта (онлайн-портал)</Typography>
            <Typography variant="caption" color="text.secondary">
              Текст договора-оферты, который видит клиент в личном кабинете. Версии и хеши сохраняются.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEditorWithActive();
            }}
            sx={{ mr: 2 }}
          >
            Редактировать
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity="info" sx={{ mb: 2 }}>
          К новым проектам договора добавляется приложение о фактической выдаче и ручном продлении.
          Оно сохраняется вместе с текстом при принятии. Уже принятые договоры не меняются.
          <RentalTerms terms={rentalTerms} />
        </Alert>
        {loadError && <Alert severity="error" sx={{mb:2}}>{loadError}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          Каждое сохранение создаёт <b>новую версию</b> с SHA-256 хешем. Старая версия деактивируется,
          но остаётся в журнале — на момент акцепта клиента в журнале событий записывается
          именно та версия, которая была активна в этот момент.
        </Alert>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Версия</TableCell>
                <TableCell>Создана</TableCell>
                <TableCell>Хеш (SHA-256)</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.length === 0 && !loading && !loadError && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">Версий пока нет</Typography>
                  </TableCell>
                </TableRow>
              )}
              {versions.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    <b>v{v.version_number}</b>
                  </TableCell>
                  <TableCell>{fmtDate(v.created_at)}</TableCell>
                  <TableCell>
                    <Tooltip title={v.text_hash}>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {shortHash(v.text_hash)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {v.is_active ? (
                      <Chip label="Активна" color="success" size="small" />
                    ) : (
                      <Chip label="Архив" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Просмотр">
                      <IconButton size="small" onClick={() => openPreview(v.id)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Открыть в редакторе">
                      <IconButton size="small" onClick={() => openEditorFromVersion(v.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!v.is_active && (
                      <Tooltip title="Сделать активной">
                        <IconButton size="small" onClick={() => handleActivate(v.id)}>
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>

      {/* ============ ДИАЛОГ РЕДАКТОРА ============ */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Редактирование оферты</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Сохранение создаст <b>новую версию</b>. Используйте плейсхолдеры в фигурных скобках:{" "}
            <code>{"{deal_number}"}</code>, <code>{"{client_name}"}</code> и т.д. Список справа.
          </Alert>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              multiline
              minRows={20}
              maxRows={30}
              fullWidth
              value={editorText}
              onChange={(e) => setEditorText(e.target.value)}
              sx={{ flex: 1, "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
            />
            <Box sx={{ width: 280, flexShrink: 0 }}>
              <Typography variant="subtitle2" gutterBottom>
                Доступные плейсхолдеры
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ maxHeight: 480, overflowY: "auto" }}>
                {PLACEHOLDERS.map((p) => (
                  <Box key={p.key} sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => insertPlaceholder(p.key)}
                      sx={{ textTransform: "none", fontFamily: "monospace", mb: 0.5 }}
                    >
                      {`{${p.key}}`}
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {p.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Сохраняю..." : "Сохранить как новую версию"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ ДИАЛОГ ПРОСМОТРА ============ */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Версия v{previewVersion?.version_number} {previewVersion?.is_active && "(активная)"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            Хеш: <code>{previewVersion?.text_hash}</code>
            <br />
            Создана: {fmtDate(previewVersion?.created_at)}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "#FAFAFA" }}>
            <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap" }}>
              {previewVersion?.text}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}
